import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { docEntityHandleHash } from "./core/handleHash";
import { emptyEntitySet } from "./entitySet";

/**
 * A Set-like collection for DocEntityHandles, leveraging docEntityHandleEquals for equality checks.
 * This implements an API similar to the standard JS Primitive Set.
 */
export class DocEntityHandleSet<T extends DocEntityHandle = DocEntityHandle> implements Iterable<T> {
  private handles: Map<string, T> = new Map();

  /**
   * Creates a new DocEntityHandleSet, optionally populated from an iterable of DocEntityHandles.
   * @param iterable An optional iterable of DocEntityHandles to add to the set.
   */
  constructor(iterable?: Iterable<T | undefined | null> | null) {
    if (iterable) {
      for (const handle of iterable) {
        if (!handle) {
          continue;
        }

        this.add(handle);
      }
    }
  }

  /**
   * Adds a handle to the set. If the handle already exists (determined by docEntityHandleEquals),
   * it will not be added again.
   * @param handle The handle to add.
   * @returns The DocEntityHandleSet object.
   */
  public add(handle: T): this {
    const hash = docEntityHandleHash(handle);
    this.handles.set(hash, handle);
    return this;
  }

  /**
   * Removes all handles from the set.
   * @returns void
   */
  public clear(): void {
    this.handles.clear();
  }

  /**
   * Removes a handle from the set.
   * @param handle The handle to remove.
   * @returns true if the handle was in the set and has been removed, false otherwise.
   */
  public delete(handle: T): boolean {
    const hash = docEntityHandleHash(handle);
    return this.handles.delete(hash);
  }

  /**
   * Returns an iterator that yields the handles in the set.
   */
  public [Symbol.iterator](): Iterator<T> {
    return this.handles.values();
  }

  /**
   * Returns an iterator of [handle, handle] pairs for every handle in the set.
   */
  public entries(): IterableIterator<[T, T]> {
    const handles = this.handles;
    function* entriesGenerator(): Generator<[T, T]> {
      for (const handle of handles.values()) {
        yield [handle, handle];
      }
    }
    return entriesGenerator();
  }

  /**
   * Executes a callback for each handle in the set.
   * @param callbackfn Function to execute for each handle.
   * @param thisArg Value to use as 'this' when executing callback.
   */
  public forEach(callbackfn: (value: T, value2: T, set: DocEntityHandleSet<T>) => void, thisArg?: any): void {
    for (const handle of this.handles.values()) {
      callbackfn.call(thisArg, handle, handle, this);
    }
  }

  /**
   * Executes a callback for each hash in the set.
   * @param callbackfn Function to execute for each hash.
   * @param thisArg Value to use as 'this' when executing callback.
   */
  public forEachHash(callbackfn: (hash: string, handle: T, set: DocEntityHandleSet<T>) => void, thisArg?: any): void {
    for (const [hash, handle] of this.handles.entries()) {
      callbackfn.call(thisArg, hash, handle, this);
    }
  }

  /**
   * Checks if a handle exists in the set (using docEntityHandleEquals).
   * @param handle The handle to check for.
   * @returns true if the handle exists in the set, false otherwise.
   */
  public has(handle: T): boolean {
    const hash = docEntityHandleHash(handle);
    return this.handles.has(hash);
  }

  /**
   * Checks if the set contains a handle of the given entity type.
   * @param entityType The entity type to check for.
   * @returns true if the set contains a handle of the given entity type, false otherwise.
   */
  public containsEntityType(entityType: DocEntityType): boolean {
    return this.some((handle) => handle.type === entityType);
  }

  /**
   * Checks if the set contains only handles of the given entity type.
   * @param entityType The entity type to check for.
   * @returns true if the set contains only handles of the given entity type, false otherwise.
   */
  public containsOnlyEntityType(entityType: DocEntityType): boolean {
    return this.every((handle) => handle.type === entityType);
  }

  /**
   * Returns all entity types in the set as an array.
   */
  public getEntityTypes(): DocEntityType[] {
    const types = new Set<DocEntityType>();

    for (const handle of this.handles.values()) {
      types.add(handle.type);
    }

    return Array.from(types);
  }

  /**
   * Returns all hashes in the set as an iterator.
   */
  public hashes(): IterableIterator<string> {
    return this.handles.keys();
  }

  /**
   * Returns all handles in the set as an iterator.
   */
  public values(): IterableIterator<T> {
    return this.handles.values();
  }

  /**
   * Returns the number of handles in the set.
   */
  public get size(): number {
    return this.handles.size;
  }

  /**
   * Returns an array containing all handles in the set.
   */
  public toArray(): T[] {
    return Array.from(this.handles.values());
  }

  /**
   * Returns an array containing all handles in the set that pass the predicate.
   */
  public toFilteredArray(predicate: (handle: T) => boolean): T[] {
    return this.toArray().filter(predicate);
  }

  /**
   * Returns an EntitySet containing all handles in the set.
   */
  public toEntitySet(): EntitySet {
    if (this.handles.size === 0) {
      return emptyEntitySet;
    }

    const set: { [hash: string]: T } = {};
    for (const [hash, handle] of this.handles) {
      set[hash] = handle;
    }
    return set as EntitySet<T>;
  }

  /**
   * Tests whether at least one handle in the set passes the test implemented by the provided function.
   * @param predicate A function that tests each handle in the set.
   * @param thisArg An object to which the this keyword can refer in the predicate function.
   * @returns true if the predicate function returns a truthy value for at least one handle in the set; otherwise, false.
   */
  public some(predicate: (value: T) => boolean, thisArg?: any): boolean {
    for (const handle of this.handles.values()) {
      if (predicate.call(thisArg, handle)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Tests whether all handles in the set pass the test implemented by the provided function.
   * @param predicate A function that tests each handle in the set.
   * @param thisArg An object to which the this keyword can refer in the predicate function.
   * @returns true if the predicate function returns a truthy value for all handles in the set; otherwise, false.
   */
  public every(predicate: (value: T) => boolean, thisArg?: any): boolean {
    for (const handle of this.handles.values()) {
      if (!predicate.call(thisArg, handle)) {
        return false;
      }
    }
    return true;
  }
}
